import { Menu } from "./Menu";
import { Model } from "./Model";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import { SummaryWindowUtils } from "../components/SummaryWindow";
import { getTimezoneInUse, TServerTimezone } from "../lib/TimezoneUtils";

const DEFAULT_FONT_SIZE = 10;
const WEIGHT = 0.3;

export type TNewUserArgs = {
    isExternalUser: boolean;
    login: string;
    firstName: string;
    lastName: string;
    email: string;
    language: string;
    dateFormat: string;
    emailStatus: number;
    reservationStatus: number;
    timezone: string;
    displayTimezones: string[];
    useCustomWeekNames: boolean;
    useSecondarySize: boolean;
    userId?: number;
    isAdmin?: boolean;
    isSuper?: boolean;
    showConfirmEntryRemovalWarning?: boolean;
    mute?: boolean;
    allowSingleClickReservation?: boolean;
    allowTemporarySingleClickReservation?: boolean;
    jumpToNextType?: boolean;
    menuPosition?: "left" | "right";
    belowbarWeights?: number[];
    preferredInfoSection?: string;
    showLists?: boolean;
    fontSize?: number;
    showExtraInfo?: boolean;
    tooltipInSidebar?: boolean;
    fieldsInSelection?: boolean;
    fieldsNextToSelection?: boolean;
    enableMultiSelect?: boolean;
    createGroupsForReservationTracks?: boolean;
    promptForGroupName?: boolean;
    recentlyUsedState?: Record<string, boolean>;
    showRoundedTimeIndicator?: boolean;
};

export class User extends Model {
    login: string;
    firstName: string;
    lastName: string;
    email: string;
    theme: string;
    language: string;
    dateFormat: string;
    reservationStatus: number;
    isExternalUser: boolean;
    emailStatus: number;
    userId: number | null;
    isAdmin: boolean;
    isSuper: boolean;
    timezone: string | null;
    actualTimezone: TServerTimezone | undefined;
    displayTimezones: string[];
    showConfirmEntryRemovalWarning: boolean;
    mute: boolean;
    allowSingleClickReservation: boolean;
    allowTemporarySingleClickReservation: boolean;
    jumpToNextType: boolean;
    menuPosition: "left" | "right";
    belowbarWeights: number[];

    preferredInfoSection: string[];
    showLists: boolean;
    hasChosenReservationInfoPosition: boolean;
    colorFieldId: number;
    reservationSummaryUnit: string;
    fontSize: number;
    showExtraInfo: boolean;
    tooltipInSidebar: boolean;
    useCustomWeekNames: boolean;
    fieldsInSelection: boolean;
    fieldsNextToSelection: boolean;
    enableMultiSelect: boolean;
    createGroupsForReservationTracks: boolean;
    promptForGroupName: boolean;
    recentlyUsedState: Record<string, boolean>;
    showRoundedTimeIndicator: boolean;
    useSecondarySize: boolean;
    database?: string | null;
    server?: string | null;

    static DEFAULT_WEIGHTS = [WEIGHT, WEIGHT];
    static PreferredSection = {
        RESERVATION_INFO: "reservation",
        OBJECT_INFO: "object",
        HISTORY: "history",
    };

    constructor(
        user: TNewUserArgs,
        serverTimezones: TServerTimezone[],
        serverDefaultTimezone?: string
    ) {
        super("User");
        // User info
        this.login = user.login;
        this.firstName = user.firstName || "";
        this.lastName = user.lastName || "";
        this.email = user.email || "";

        // Settings
        this.theme = "";
        this.language = user.language || "";
        this.dateFormat = user.dateFormat || "";
        this.reservationStatus = user.reservationStatus;
        this.isExternalUser = user.isExternalUser || false;
        this.emailStatus = user.emailStatus;
        this.userId = user.userId || null;
        this.isAdmin = user.isAdmin || false;
        this.isSuper = user.isSuper || false;
        this.timezone = user.timezone || null;
        this.actualTimezone = getTimezoneInUse(
            this.timezone,
            serverTimezones,
            serverDefaultTimezone
        );
        this.displayTimezones = user.displayTimezones || [];

        this.showConfirmEntryRemovalWarning =
            user.showConfirmEntryRemovalWarning !== undefined
                ? user.showConfirmEntryRemovalWarning
                : true;
        this.mute = user.mute !== undefined ? user.mute : true;
        this.allowSingleClickReservation = user.allowSingleClickReservation || false;
        // Temporary activation of single click mode, should never be saved
        this.allowTemporarySingleClickReservation =
            user.allowTemporarySingleClickReservation || false;
        this.jumpToNextType = user.jumpToNextType !== undefined ? user.jumpToNextType : true;
        this.menuPosition =
            user.menuPosition === Menu.POSITION.LEFT ? Menu.POSITION.LEFT : Menu.POSITION.RIGHT;
        this.belowbarWeights = user.belowbarWeights || User.DEFAULT_WEIGHTS;
        const pfi = user.preferredInfoSection || User.PreferredSection.RESERVATION_INFO;
        if (!Array.isArray(pfi)) {
            this.preferredInfoSection = [pfi];
        } else {
            this.preferredInfoSection = pfi;
        }
        this.showLists = user.showLists !== undefined ? user.showLists : true;
        this.hasChosenReservationInfoPosition = true;
        this.colorFieldId = 79;
        this.reservationSummaryUnit = SummaryWindowUtils.HOURS;
        this.fontSize = user.fontSize || DEFAULT_FONT_SIZE;
        this.showExtraInfo = user.showExtraInfo || false;
        this.tooltipInSidebar = user.tooltipInSidebar || false;
        this.useCustomWeekNames = user.useCustomWeekNames || false; // TODO The server will have a default value, should be used instead of false, right?
        this.fieldsInSelection = user.fieldsInSelection || false;
        this.fieldsNextToSelection = user.fieldsNextToSelection || false;
        this.enableMultiSelect = user.enableMultiSelect || false;
        this.createGroupsForReservationTracks =
            user.createGroupsForReservationTracks !== undefined
                ? user.createGroupsForReservationTracks
                : true;
        this.promptForGroupName =
            user.promptForGroupName !== undefined ? user.promptForGroupName : true;
        this.recentlyUsedState = user.recentlyUsedState || {};
        this.showRoundedTimeIndicator = user.showRoundedTimeIndicator || false;
        this.useSecondarySize = user.useSecondarySize || false;
    }

    /** serverDefaultTimezone: the short name of the timezone the server uses by default. */
    static getCurrent(cb, serverDefaultTimezone?: string) {
        let user: User;
        const otherPrefs: Record<string, any> = {};
        _.runAsync(
            [
                (done) =>
                    API.getTimezones((timezones) => {
                        API.getCurrentUserPrefs((userData) => {
                            user = new User(userData, timezones, serverDefaultTimezone);
                            API.getAuthenticatedUserInfo((userInformation) => {
                                const userInfo = userInformation.parameters[0];
                                user.userId = userInfo.id;
                                user.isAdmin = userInfo.client_admin || false;
                                user.isSuper = userInfo.client_super || false;
                                user.server = userInfo.server || null;
                                user.database = userInfo.database || null;
                                done();
                            });
                        });
                    }),
                (done) =>
                    API.getOtherUserPrefs((morePrefs) => {
                        Object.keys(morePrefs).forEach((key) => {
                            otherPrefs[key] = morePrefs[key];
                        });
                        API.getPreferences(
                            "useSecondarySize",
                            undefined,
                            undefined,
                            (doUseSecondarySize) => {
                                otherPrefs.useSecondarySize = doUseSecondarySize || false;
                                done();
                            }
                        );
                    }),
            ],
            () => cb(_.extend(user, otherPrefs))
        );
    }

    allowsSingleClickReservation() {
        return this.allowSingleClickReservation || this.allowTemporarySingleClickReservation;
    }

    setBelowbarWeights(weights) {
        API.setPreferences(
            "belowbarWeights",
            [JSON.stringify({ objectWeight: weights[0], typeWeight: weights[1] })],
            _.noop
        );
        return this.immutableSet({ belowbarWeights: weights });
    }

    setPreferredInfoSection(section = []) {
        API.setPreferences("preferredInfoSection", [JSON.stringify(section)], _.noop);
        return this.immutableSet({ preferredInfoSection: section });
    }

    setMenuPosition(position) {
        API.setPreferences("menuPosition", [position], _.noop);
        return this.immutableSet({ menuPosition: position });
    }

    setReservationSummaryUnit(unit: string) {
        API.setPreferences("reservationSummaryUnit", [unit], _.noop);
        return this.immutableSet({ reservationSummaryUnit: unit });
    }

    setFontSize(newSize) {
        return this.immutableSet({ fontSize: newSize });
    }

    getColorFieldId() {
        return this.colorFieldId;
    }

    // Returns true, false or undefined. Undefined means "use default behavior".
    isRecentlyUsedChecked(typeId): boolean | undefined {
        return this.recentlyUsedState[typeId];
    }

    setRecentlyUsedChecked(typeId, checked) {
        const newState = _.extend({}, this.recentlyUsedState, { [typeId]: checked });
        API.setPreferences("recentlyUsedState", [JSON.stringify(newState)], _.noop);
        return this.immutableSet({ recentlyUsedState: newState });
    }

    clearRecentlyUsedChecks() {
        API.setPreferences("recentlyUsedState", [JSON.stringify({})], _.noop);
        return this.immutableSet({ recentlyUsedState: {} });
    }

    save(cb) {
        _.runAsync(
            [
                (done) =>
                    API.setPreferences(
                        "allowSingleClickReservation",
                        [this.allowSingleClickReservation],
                        done
                    ),
                (done) => API.setPreferences("jumpToNextType", [this.jumpToNextType], done),
                (done) => API.setPreferences("mute", [this.mute], done),
                (done) =>
                    API.setPreferences(
                        "dismissedModalDialogs.confirm_entry_removal",
                        [this.showConfirmEntryRemovalWarning],
                        done
                    ),
                (done) => API.setPreferences("colorFieldId", [this.colorFieldId], done),
                (done) => API.setPreferences("fontSize", [this.fontSize], done),
                (done) => API.setPreferences("showExtraInfo", [this.showExtraInfo], done),
                (done) => API.setPreferences("showLists", [this.showLists], done),
                (done) => API.setPreferences("tooltipInSidebar", [this.tooltipInSidebar], done),
                (done) => API.setPreferences("fieldsInSelection", [this.fieldsInSelection], done),
                (done) => API.setPreferences("enableMultiSelect", [this.enableMultiSelect], done),
                (done) =>
                    API.setPreferences("fieldsNextToSelection", [this.fieldsNextToSelection], done),
                (done) =>
                    API.setPreferences(
                        "createGroupsForReservationTracks",
                        [this.createGroupsForReservationTracks],
                        done
                    ),
                (done) => API.setPreferences("promptForGroupName", [this.promptForGroupName], done),
                (done) =>
                    API.setPreferences(
                        "showRoundedTimeIndicator",
                        [this.showRoundedTimeIndicator],
                        done
                    ),
                (done) => API.setPreferences("useSecondarySize", [this.useSecondarySize], done),
            ],
            () => API.setCurrentUserPrefs(this, cb)
        );
    }
}
